import { sharedApiGet } from "./shared-api-data";
import { installDeferredApiLoading } from "./deferred-api-loading";
import { joinApiUrl } from "../../config/api-url";
import { isApiVisible } from "./api-visibility";
import { Formio } from "formiojs";

const SelectComponent =
    Formio.Components.components.select;

/**
 * Liest einen Wert über einen Punktpfad.
 *
 * Beispiele:
 * getByPath(row, "identifier")
 * getByPath(response, "data.roles")
 */
function getByPath(object, path) {
    if (!path) {
        return object;
    }

    return String(path)
        .split(".")
        .filter(Boolean)
        .reduce((current, key) => {
            if (
                current === null ||
                current === undefined
            ) {
                return undefined;
            }

            return current[key];
        }, object);
}

/**
 * Ersetzt Platzhalter innerhalb eines Endpoints.
 *
 * Beispiel:
 * /api/users/{userId}
 */
function resolveTemplate(template, data) {
    return String(template || "").replace(
        /\{([^}]+)\}/g,
        (match, key) => {
            const value = getByPath(
                data,
                key.trim()
            );

            if (
                value === null ||
                value === undefined
            ) {
                return "";
            }

            return encodeURIComponent(
                String(value)
            );
        }
    );
}

/**
 * Erstellt eine tiefe Kopie einfacher JSON-Daten.
 */
function deepClone(value) {
    if (
        value === null ||
        value === undefined
    ) {
        return value;
    }

    return JSON.parse(
        JSON.stringify(value)
    );
}

/**
 * Wandelt Text, Arrays oder Tags-Objekte in
 * ein einheitliches String-Array um.
 *
 * Unterstützt:
 * - "A, B, C"
 * - ["A", "B", "C"]
 * - [{ value: "A" }]
 */
function normalizeList(value) {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return [];
    }

    let sourceValues = [];

    if (Array.isArray(value)) {
        sourceValues = value;
    } else if (typeof value === "string") {
        sourceValues = value.split(",");
    } else {
        sourceValues = [value];
    }

    return sourceValues
        .map((entry) => {
            if (
                entry &&
                typeof entry === "object"
            ) {
                if (
                    entry.value !== null &&
                    entry.value !== undefined
                ) {
                    return String(
                        entry.value
                    ).trim();
                }

                if (
                    entry.label !== null &&
                    entry.label !== undefined
                ) {
                    return String(
                        entry.label
                    ).trim();
                }
            }

            return String(entry).trim();
        })
        .filter(Boolean);
}

/**
 * Liest die Abhängigkeiten.
 *
 * Neue Konfiguration:
 * api.dependsOnText =
 * "permissionScopeType, permissionTargetId"
 *
 * Alte Array-Konfiguration wird weiterhin unterstützt.
 */
function normalizeDependsOn(config) {
    if (
        typeof config.dependsOnText ===
        "string" &&
        config.dependsOnText.trim() !== ""
    ) {
        return normalizeList(
            config.dependsOnText
        );
    }

    return normalizeList(
        config.dependsOn
    );
}

/**
 * Liest die Whitelist.
 *
 * Neue Konfiguration:
 * api.includeValuesText =
 * "COMMUNITY_ADMIN, COMMUNITY_MEMBER"
 */
function normalizeIncludeValues(config) {
    if (
        typeof config.includeValuesText ===
        "string" &&
        config.includeValuesText.trim() !== ""
    ) {
        return normalizeList(
            config.includeValuesText
        );
    }

    return normalizeList(
        config.includeValues
    );
}

/**
 * Liest die Blacklist.
 *
 * Neue Konfiguration:
 * api.excludeValuesText = "SYSTEM_ADMIN"
 */
function normalizeExcludeValues(config) {
    if (
        typeof config.excludeValuesText ===
        "string" &&
        config.excludeValuesText.trim() !== ""
    ) {
        return normalizeList(
            config.excludeValuesText
        );
    }

    return normalizeList(
        config.excludeValues
    );
}

/**
 * Vergleicht Filterwerte.
 */
function valuesMatch(
    currentValue,
    configuredValue,
    ignoreCase
) {
    const left =
        currentValue === null ||
        currentValue === undefined ?
        "" :
        String(currentValue);

    const right =
        configuredValue === null ||
        configuredValue === undefined ?
        "" :
        String(configuredValue);

    if (ignoreCase === true) {
        return (
            left.toLowerCase() ===
            right.toLowerCase()
        );
    }

    return left === right;
}

/**
 * Erzeugt eine lesbare Fehlermeldung.
 */
function normalizeAxiosError(error) {
    return (
        error ?.response ?.data ?.messageDe ||
        error ?.response ?.data ?.Message ||
        error ?.response ?.data ?.message ||
        error ?.message ||
        "Die Daten konnten nicht geladen werden."
    );
}

export default class ApiSelectComponent
extends SelectComponent {

    static schema(...extend) {
        return SelectComponent.schema({
                type: "apiSelect",
                label: "API Select",
                key: "apiSelect",

                input: true,
                tableView: true,
                persistent: false,

                widget: "choicesjs",
                dataSrc: "values",
                valueProperty: "value",
                template: "<span>{{ item.label }}</span>",

                lazyLoad: false,
                authenticate: true,
                clearOnRefresh: false,
                clearOnHide: false,
                refreshOn: "",
                multiple: false,

                api: {
                    endpoint: "",
                    method: "GET",
                    resultPath: "",

                    labelField: "label",
                    valueField: "value",

                    /*
                     * Neue Textkonfiguration.
                     */
                    dependsOnText: "",
                    includeValuesText: "",
                    excludeValuesText: "",

                    /*
                     * Alte Array-Konfiguration bleibt
                     * aus Kompatibilitätsgründen erhalten.
                     */
                    dependsOn: [],
                    includeValues: [],
                    excludeValues: [],

                    autoLoad: true,

                    filterField: "",
                    filterIgnoreCase: true
                }
            },
            ...extend
        );
    }

    static get builderInfo() {
        return {
            title: "API Select",
            icon: "list",
            group: "basic",
            weight: 25,
            schema: ApiSelectComponent.schema()
        };
    }

    constructor(
        component,
        options,
        data
    ) {
        // Keep the existing saved dashboard panel compatible without reimport.
        if (/^\/?api\/admin\/statistics\/options\/(communities|forms)$/.test(component.api?.endpoint || "")) {
            let customOptions = component.customOptions || {};
            if (typeof customOptions === "string") {
                try { customOptions = JSON.parse(customOptions); }
                catch { customOptions = {}; }
            }
            component = { ...component, dropdown: "bottom", customOptions: { ...customOptions, position: "bottom" } };
        }
        super(
            component,
            options,
            data
        );

        this._apiLoading = false;
        this._apiLoadedSignature = null;
        this._apiError = null;
        this._apiItems = [];

        this._rootChangeHandler = null;
        this._dependencyChangeTimer = null;
    }

    init() {
        super.init();
    }

    get apiConfig() {
        return this.component.api || {};
    }

    /**
     * Kombiniert globale Formulardaten mit dem lokalen Datenkontext.
     * Lokale Zeilenwerte überschreiben gleichnamige globale Werte.
     */
    getRequestData() {
        let rootData = {};

        if (
            this.root &&
            this.root.data &&
            typeof this.root.data === "object"
        ) {
            rootData = this.root.data;
        } else if (
            this.root &&
            this.root.submission &&
            this.root.submission.data &&
            typeof this.root.submission.data === "object"
        ) {
            rootData = this.root.submission.data;
        }

        return {
            ...(rootData || {}),
            ...(this.data || {})
        };
    }

    get isBuilderMode() {
        return !!(
            window.__OZG_FORMIO_BUILDER_MODE === true ||
            (this.options && this.options.builder === true) ||
            (this.root && this.root.options && this.root.options.builder === true)
        );
    }

    /**
     * Kennzeichnet den aktuellen Ladezustand.
     * Änderungen an Endpoint, Abhängigkeiten oder
     * Filtern erzeugen eine neue Signatur.
     */
    get requestSignature() {
        const config =
            this.apiConfig;

        const endpoint =
            resolveTemplate(
                config.endpoint,
                this.getRequestData()
            );

        const dependsOn =
            normalizeDependsOn(config);

        const dependencyValues =
            dependsOn.map((key) => {
                return getByPath(
                    this.getRequestData(),
                    key
                );
            });

        return JSON.stringify({
            endpoint,
            method: String(
                config.method || "GET"
            ).toUpperCase(),

            dependencyValues,

            filterField: config.filterField || "",

            includeValues: normalizeIncludeValues(
                config
            ),

            excludeValues: normalizeExcludeValues(
                config
            ),

            filterIgnoreCase: config.filterIgnoreCase !== false
        });
    }

    /**
     * Prüft, ob die API geladen werden darf.
     */
    shouldLoad() {
        if (this.isBuilderMode) {
            return false;
        }



        const config =
            this.apiConfig;

        if (
            config.autoLoad === false
        ) {
            return false;
        }

        if (!config.endpoint) {
            return false;
        }

        const dependsOn =
            normalizeDependsOn(config);

        return dependsOn.every(
            (key) => {
                const value =
                    getByPath(
                        this.getRequestData(),
                        key
                    );

                return (
                    value !== null &&
                    value !== undefined &&
                    value !== ""
                );
            }
        );
    }

    getAxios() {
        const axios =
            window.GLOBALAXIOS;

        if (!axios) {
            throw new Error(
                "GLOBALAXIOS ist nicht verfügbar."
            );
        }

        return axios;
    }

    getApiBaseUrl() {
        return (
            window.GLOBALCONFIG ?.API_URL ||
            ""
        );
    }

    /**
     * Führt GET oder POST aus.
     */
    async executeRequest(
        method,
        endpoint,
        body
    ) {
        // Letzte zentrale Schranke: Im Form.io Builder niemals HTTP ausführen.
        if (this.isBuilderMode) {
            console.debug(
                "[ApiSelect] API-Aufruf im Builder blockiert:",
                endpoint
            );

            return {
                status: 200,
                data: []
            };
        }

        const axios =
            this.getAxios();

        const url =
            joinApiUrl(this.getApiBaseUrl(), endpoint);

        const normalizedMethod =
            String(
                method || "GET"
            ).toUpperCase();

        if (
            normalizedMethod === "POST"
        ) {
            return axios.post(
                url,
                body || {}
            );
        }

        return sharedApiGet(axios, url);
    }

    /**
     * Filtert die API-Daten.
     */
    filterRows(rows) {
        const config =
            this.apiConfig;

        const filterField =
            String(
                config.filterField || ""
            ).trim();

        const includeValues =
            normalizeIncludeValues(
                config
            );

        const excludeValues =
            normalizeExcludeValues(
                config
            );

        const ignoreCase =
            config.filterIgnoreCase !== false;

        /*
         * Ohne Filterfeld kann keine Filterung
         * auf DTO-Feldebene erfolgen.
         */
        if (!filterField) {
            return rows;
        }

        return rows.filter((row) => {
            const currentValue =
                getByPath(
                    row,
                    filterField
                );

            /*
             * Whitelist.
             */
            if (
                includeValues.length > 0
            ) {
                const isIncluded =
                    includeValues.some(
                        (configuredValue) => {
                            return valuesMatch(
                                currentValue,
                                configuredValue,
                                ignoreCase
                            );
                        }
                    );

                if (!isIncluded) {
                    return false;
                }
            }

            /*
             * Blacklist.
             */
            if (
                excludeValues.length > 0
            ) {
                const isExcluded =
                    excludeValues.some(
                        (configuredValue) => {
                            return valuesMatch(
                                currentValue,
                                configuredValue,
                                ignoreCase
                            );
                        }
                    );

                if (isExcluded) {
                    return false;
                }
            }

            return true;
        });
    }

    /**
     * Wandelt API-Zeilen in Form.io-Auswahlwerte um.
     */
    mapRowsToItems(rows) {
        const config =
            this.apiConfig;

        const labelField =
            config.labelField || "label";

        const valueField =
            config.valueField || "value";

        return rows.map((row) => {
            const label =
                getByPath(
                    row,
                    labelField
                );

            const value =
                getByPath(
                    row,
                    valueField
                );

            return {
                label: label === null ||
                    label === undefined ?
                    "" : String(label),

                value,

                original: deepClone(row)
            };
        });
    }

    /**
     * Lädt die API-Auswahlwerte.
     */
    async loadApiItems(
        force = false
    ) {
        // Dashboard choices are fetched on first opening, not on page attach.
        const statisticsOptions = /^\/?api\/admin\/statistics\/options\//.test(this.apiConfig.endpoint || "");
        if (statisticsOptions &&
            (!this._statisticsOptionsOpened || !isApiVisible(this))) return;
        if (
            this._apiLoading ||
            !this.shouldLoad()
        ) {
            return;
        }

        const signature =
            this.requestSignature;

        if (
            force !== true &&
            this._apiLoadedSignature ===
            signature
        ) {
            return;
        }

        const config =
            this.apiConfig;

        const endpoint =
            resolveTemplate(
                config.endpoint,
                this.getRequestData()
            );

        const method =
            String(
                config.method || "GET"
            ).toUpperCase();

        this._apiLoading = true;
        this._apiError = null;

        try {
            const response =
                await this.executeRequest(
                    method,
                    endpoint,
                    config.body || {}
                );

            const result =
                getByPath(
                    response.data,
                    config.resultPath
                );

            const rows =
                Array.isArray(result) ?
                result : [];

            const filteredRows =
                this.filterRows(rows);

            const items =
                this.mapRowsToItems(
                    filteredRows
                );

            this._apiItems =
                deepClone(items);

            this.component.data =
                this.component.data || {};

            this.component.data.values =
                deepClone(items);

            if (statisticsOptions) this._apiLoadedSignature = signature;

            if (
                typeof this.setItems ===
                "function"
            ) {
                this.setItems(
                    deepClone(items)
                );
            }

            this._apiLoadedSignature =
                signature;

            /*
             * Das übergeordnete EditGrid neu zeichnen,
             * damit statt roleId das Rollenlabel erscheint.
             */
            if (!statisticsOptions && (
                this.parent &&
                typeof this.parent.redraw === "function"
            )) {
                this.parent.redraw();
            } else if (!statisticsOptions && (
                typeof this.redraw === "function"
            )) {
                this.redraw();
            }

        } catch (error) {
            /*
             * Fehlgeschlagene Anfrage für diese Signatur als verarbeitet
             * markieren. Dadurch starten setItems(), updateValue() oder
             * erneutes attach() denselben 401/403/500 nicht sofort erneut.
             *
             * reloadApiItems() kann weiterhin bewusst force=true verwenden.
             */
            this._apiLoadedSignature =
                signature;

            this._apiError =
                normalizeAxiosError(error);

            this._apiItems = [];

            this.component.data =
                this.component.data || {};

            this.component.data.values = [];

            if (
                typeof this.setItems ===
                "function"
            ) {
                this.setItems([]);
            }

            console.warn(
                "[ApiSelect] Laden fehlgeschlagen", {
                    key: this.component.key,
                    endpoint,
                    error
                }
            );
        } finally {
            this._apiLoading = false;
        }
    }

    /**
     * Initialisiert API-Laden und Dropdown-Ebene.
     */
    attach(element) {
        const attached =
            super.attach(element);

        if (/^\/?api\/admin\/statistics\/options\//.test(this.apiConfig.endpoint || "")) {
            this.addEventListener(element, "showDropdown", () => {
                this._statisticsOptionsOpened = true;
                this.loadApiItems(false);
            });
        }

        window.setTimeout(() => {
            if (this.isBuilderMode) {
                // Im Builder weder laden noch bereits vorhandene API-Optionen anzeigen.
                this._apiItems = [];
                this.component.data = this.component.data || {};
                this.component.data.values = [];

                if (typeof this.setItems === "function") {
                    this.setItems([]);
                }

                return;
            }

            this.loadApiItems(false);
        }, 0);

        /*
         * Abhängigkeiten im gesamten Form.io-Formular beobachten.
         * Die Signatur entscheidet, ob tatsächlich neu geladen werden muss.
         */
        if (
            !this.isBuilderMode &&
            normalizeDependsOn(this.apiConfig).length > 0 &&
            this.root &&
            typeof this.root.on === "function" &&
            !this._rootChangeHandler
        ) {
            this._rootChangeHandler = () => {
                window.clearTimeout(this._dependencyChangeTimer);

                this._dependencyChangeTimer = window.setTimeout(() => {
                    this._dependencyChangeTimer = null;

                    const nextSignature = this.requestSignature;

                    if (
                        nextSignature === this._apiLoadedSignature ||
                        this._apiLoading
                    ) {
                        return;
                    }

                    this._apiLoadedSignature = null;

                    if (!this.shouldLoad()) {
                        this._apiItems = [];
                        this.component.data = this.component.data || {};
                        this.component.data.values = [];

                        if (typeof this.setItems === "function") {
                            this.setItems([]);
                        }

                        return;
                    }

                    this.loadApiItems(true);
                }, 75);
            };

            this.root.on("change", this._rootChangeHandler);
        }

        /*
         * Dropdown innerhalb eines ApiEditGrid
         * über die folgenden Tabellenzeilen heben.
         */
        const choicesElement =
            element.querySelector(
                ".choices"
            );

        const tableCell =
            element.closest("td");

        const tableRow =
            element.closest("tr");

        const editGrid =
            element.closest(
                ".api-edit-grid"
            );

        const activateDropdownLayer =
            () => {
                if (choicesElement) {
                    choicesElement.classList.add(
                        "api-select-dropdown-open"
                    );
                }

                if (tableCell) {
                    tableCell.classList.add(
                        "api-select-cell-open"
                    );
                }

                if (tableRow) {
                    tableRow.classList.add(
                        "api-select-row-open"
                    );
                }

                if (editGrid) {
                    editGrid.classList.add(
                        "api-select-active"
                    );
                }
            };

        const deactivateDropdownLayer =
            () => {
                window.setTimeout(() => {
                    const stillOpen =
                        choicesElement &&
                        choicesElement.classList.contains(
                            "is-open"
                        );

                    if (stillOpen) {
                        return;
                    }

                    if (choicesElement) {
                        choicesElement.classList.remove(
                            "api-select-dropdown-open"
                        );
                    }

                    if (tableCell) {
                        tableCell.classList.remove(
                            "api-select-cell-open"
                        );
                    }

                    if (tableRow) {
                        tableRow.classList.remove(
                            "api-select-row-open"
                        );
                    }

                    if (editGrid) {
                        editGrid.classList.remove(
                            "api-select-active"
                        );
                    }
                }, 50);
            };

        const selectInput =
            element.querySelector(
                "select"
            );

        if (selectInput) {
            this.addEventListener(
                selectInput,
                "showDropdown",
                activateDropdownLayer
            );

            this.addEventListener(
                selectInput,
                "hideDropdown",
                deactivateDropdownLayer
            );
        }

        /*
         * Fallback für unterschiedliche
         * Choices.js-/Form.io-Versionen.
         */
        if (choicesElement) {
            this.addEventListener(
                choicesElement,
                "mousedown",
                activateDropdownLayer
            );

            this.addEventListener(
                choicesElement,
                "focusin",
                activateDropdownLayer
            );

            this.addEventListener(
                choicesElement,
                "focusout",
                deactivateDropdownLayer
            );
        }

        return attached;
    }

    /**
     * Prüft bei Formularänderungen, ob sich
     * Endpoint oder Abhängigkeiten geändert haben.
     */
    updateValue(
        value,
        flags = {}
    ) {
        const changed =
            super.updateValue(
                value,
                flags
            );

        if (this.isBuilderMode) {
            return changed;
        }

        const signature =
            this.requestSignature;

        if (
            signature !==
            this._apiLoadedSignature &&
            !this._apiLoading
        ) {
            window.setTimeout(() => {
                this.loadApiItems(true);
            }, 0);
        }

        return changed;
    }

    /**
     * Sucht zu einem gespeicherten Wert das passende API-Element.
     */
    findApiItemByValue(value) {
        const items =
            Array.isArray(this._apiItems) ?
            this._apiItems : [];

        return items.find((item) => {
            return String(item.value) === String(value);
        });
    }

    /**
     * Liefert das Label für einen einzelnen gespeicherten Wert.
     */
    getApiLabelForValue(value) {
        if (
            value === null ||
            value === undefined ||
            value === ""
        ) {
            return "";
        }

        const item =
            this.findApiItemByValue(value);

        if (item) {
            return item.label;
        }

        /*
         * Fallback auf die Werte, die Form.io
         * bereits in component.data.values hält.
         */
        const configuredValues =
            this.component &&
            this.component.data &&
            Array.isArray(
                this.component.data.values
            ) ?
            this.component.data.values : [];

        const configuredItem =
            configuredValues.find((entry) => {
                return (
                    String(entry.value) ===
                    String(value)
                );
            });

        if (configuredItem) {
            return configuredItem.label;
        }

        return String(value);
    }

    /**
     * Darstellung in EditGrid-, DataGrid- und Tabellenansichten.
     */
    getValueAsString(value) {
        if (Array.isArray(value)) {
            return value
                .map((entry) =>
                    this.getApiLabelForValue(entry)
                )
                .filter(Boolean)
                .join(", ");
        }

        return this.getApiLabelForValue(value);
    }

    /**
     * Einige Form.io-Versionen verwenden getView()
     * statt getValueAsString().
     */
    getView(value) {
        return this.getValueAsString(value);
    }

    /**
     * Ermöglicht anderen Komponenten,
     * die Auswahlwerte neu zu laden.
     */
    reloadApiItems() {
        this._apiLoadedSignature = null;

        return this.loadApiItems(true);
    }

    destroy() {
        window.clearTimeout(this._dependencyChangeTimer);

        if (
            this._rootChangeHandler &&
            this.root &&
            typeof this.root.off === "function"
        ) {
            this.root.off("change", this._rootChangeHandler);
        }

        this._rootChangeHandler = null;
        this._dependencyChangeTimer = null;

        return super.destroy();
    }
}

/**
 * Konfiguration im Form.io-Editor.
 */
ApiSelectComponent.editForm =
    function() {
        return {
            components: [{
                type: "tabs",
                key: "tabs",
                components: [{
                        key: "display",
                        label: "Anzeige",
                        components: [{
                                type: "textfield",
                                key: "label",
                                label: "Label"
                            },
                            {
                                type: "textfield",
                                key: "key",
                                label: "API-Key",
                                validate: {
                                    required: true
                                }
                            },
                            {
                                type: "checkbox",
                                key: "multiple",
                                label: "Mehrfachauswahl"
                            },
                            {
                                type: "checkbox",
                                key: "disabled",
                                label: "Deaktiviert"
                            },
                            {
                                type: "checkbox",
                                key: "persistent",
                                label: "In Formulardaten speichern",
                                defaultValue: false
                            },
                            {
                                type: "checkbox",
                                key: "clearOnHide",
                                label: "Wert beim Ausblenden löschen",
                                defaultValue: false
                            }
                        ]
                    },
                    {
                        key: "api",
                        label: "API",
                        components: [{
                                type: "textfield",
                                key: "api.endpoint",
                                label: "Endpoint",
                                placeholder: "/api/permission-management/roles",
                                validate: {
                                    required: true
                                }
                            },
                            {
                                type: "select",
                                key: "api.method",
                                label: "Methode",
                                data: {
                                    values: [{
                                            label: "GET",
                                            value: "GET"
                                        },
                                        {
                                            label: "POST",
                                            value: "POST"
                                        }
                                    ]
                                },
                                defaultValue: "GET"
                            },
                            {
                                type: "textfield",
                                key: "api.resultPath",
                                label: "Pfad zum Ergebnis-Array",
                                placeholder: "roles"
                            },
                            {
                                type: "textfield",
                                key: "api.labelField",
                                label: "Label-Feld",
                                placeholder: "title",
                                defaultValue: "label"
                            },
                            {
                                type: "textfield",
                                key: "api.valueField",
                                label: "Wert-Feld",
                                placeholder: "roleId",
                                defaultValue: "value"
                            },
                            {
                                type: "textfield",
                                key: "api.dependsOnText",
                                label: "Abhängige Form.io-Felder",
                                placeholder: "permissionScopeType, permissionTargetId",
                                description: "Mehrere Feldschlüssel durch Komma trennen. Leer lassen, wenn keine Abhängigkeit besteht."
                            },
                            {
                                type: "checkbox",
                                key: "api.autoLoad",
                                label: "Automatisch laden",
                                defaultValue: true
                            }
                        ]
                    },
                    {
                        key: "filter",
                        label: "Filter",
                        components: [{
                                type: "textfield",
                                key: "api.filterField",
                                label: "Filterfeld",
                                placeholder: "identifier",
                                description: "DTO-Feld, anhand dessen die API-Ergebnisse gefiltert werden."
                            },
                            {
                                type: "textfield",
                                key: "api.includeValuesText",
                                label: "Nur diese Werte anzeigen",
                                placeholder: "COMMUNITY_ADMIN, COMMUNITY_MEMBER, TESTER, FORM_BUILDER",
                                description: "Whitelist mit kommaseparierten Werten. Leer bedeutet: alle Werte zulassen."
                            },
                            {
                                type: "textfield",
                                key: "api.excludeValuesText",
                                label: "Diese Werte ausblenden",
                                placeholder: "SYSTEM_ADMIN",
                                description: "Blacklist mit kommaseparierten Werten."
                            },
                            {
                                type: "checkbox",
                                key: "api.filterIgnoreCase",
                                label: "Groß-/Kleinschreibung ignorieren",
                                defaultValue: true
                            }
                        ]
                    }
                ]
            }]
        };
    };

installDeferredApiLoading(ApiSelectComponent, ['loadApiItems']);

Formio.Components.addComponent(
    "apiSelect",
    ApiSelectComponent
);
